import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { BreadcrumbViewModel } from '../models/BreadcrumbViewModel';
import type { CheckAvailabilityViewModel } from '../models/CheckAvailabilityViewModel';
import type { ExtraServicePrebookRequestViewModel } from '../models/ExtraServicePrebookRequestViewModel';
import type { ExtraServicePrebookViewModel } from '../models/ExtraServicePrebookViewModel';
import type { ExtraServiceSearchViewModel } from '../models/ExtraServiceSearchViewModel';
import type { HotelDetailReviewViewModel } from '../models/HotelDetailReviewViewModel';
import type { HotelDetailViewModel } from '../models/HotelDetailViewModel';
import type { HotelEventsPaginatedModel } from '../models/HotelEventsPaginatedModel';
import type { HotelListViewModel } from '../models/HotelListViewModel';
import type { HotelPriceViewModel } from '../models/HotelPriceViewModel';
import type { HotelRecommendationsRequestViewModel } from '../models/HotelRecommendationsRequestViewModel';
import type { HotelUrlParserViewModel } from '../models/HotelUrlParserViewModel';
import type { RecommendedHotelsViewModel } from '../models/RecommendedHotelsViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class HotelsService {
  /**
   * List
   * Gets all hotels by slug
   * @returns HotelListViewModel Success
   * @throws ApiError
   */
  public static async listHotels(
    {
      keywords,
      slug,
      _in,
      out,
      sort,
      region,
      popularity,
      theme,
      feature,
      boardType,
      chain,
      star,
      point,
      category,
      refundable,
      status,
      priceMin,
      priceMax,
      room,
      page,
      size,
      basketId,
    }: {
      keywords?: string;
      slug?: string;
      _in?: string;
      out?: string;
      /**
       * Sort: popularity, priceasc or pricedesc, Default: popularity
       */
      sort?: any;
      region?: Array<number>;
      popularity?: Array<number>;
      theme?: Array<number>;
      feature?: Array<number>;
      boardType?: Array<number>;
      chain?: Array<number>;
      star?: Array<number>;
      point?: number;
      category?: Array<number>;
      refundable?: boolean;
      status?: boolean;
      priceMin?: number;
      priceMax?: number;
      room?: Array<string>;
      page?: number;
      size?: number;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelListViewModel>({
      method: 'GET',
      url: `/v3/hotels`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(keywords ? { keywords: keywords } : {}),
        ...(slug ? { slug: slug } : {}),
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(region ? { region: region } : {}),
        ...(popularity ? { popularity: popularity } : {}),
        ...(theme ? { theme: theme } : {}),
        ...(feature ? { feature: feature } : {}),
        ...(boardType ? { boardType: boardType } : {}),
        ...(chain ? { chain: chain } : {}),
        ...(star ? { star: star } : {}),
        ...(point ? { point: point } : {}),
        ...(category ? { category: category } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(status ? { status: status } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(room ? { room: room } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Detail
   * Gets a specific hotel by slug
   * @returns HotelDetailViewModel Success
   * @throws ApiError
   */
  public static async getHotel(
    {
      slug,
      room,
      _in,
      out,
      basketId,
    }: {
      /**
       * Hotel Slug
       */
      slug: string;
      /**
       * Room Information
       */
      room: Array<string>;
      /**
       * Checkin date
       */
      _in?: string;
      /**
       * Checkout date
       */
      out?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelDetailViewModel>({
      method: 'GET',
      url: `/v3/hotels/${slug}`,
      path: {
        ...(slug ? { slug: slug } : {}),
      },
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(room ? { room: room } : {}),
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Prices
   * Gets a specific hotel season prices
   * @returns HotelPriceViewModel Success
   * @throws ApiError
   */
  public static async getHotelPrices(
    {
      id,
    }: {
      /**
       * Hotel Id
       */
      id: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelPriceViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelPriceViewModel>({
      method: 'GET',
      url: `/v3/hotels/${id}/prices`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Prebook (Extra Service)
   * Add prebook for extra services
   * @returns ExtraServicePrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookAddExtraService(
    {
      body,
    }: {
      body?: ExtraServicePrebookRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ExtraServicePrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<ExtraServicePrebookViewModel>({
      method: 'POST',
      url: `/v3/hotels/prebook/extraservice`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Search (Extra service)
   * Search for extra services
   * @returns ExtraServiceSearchViewModel Success
   * @throws ApiError
   */
  public static async searchExtraService(
    {
      _in,
      out,
      hotelId,
      roomId,
      numberOfPeople,
      currency,
      hotelPrice,
      refundable,
      provider,
    }: {
      _in?: string;
      out?: string;
      hotelId?: number;
      roomId?: number;
      numberOfPeople?: number;
      currency?: string;
      hotelPrice?: number;
      refundable?: boolean;
      provider?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ExtraServiceSearchViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<ExtraServiceSearchViewModel>({
      method: 'GET',
      url: `/v3/hotels/search/extraservice`,
      query: {
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(hotelId ? { hotelId: hotelId } : {}),
        ...(roomId ? { roomId: roomId } : {}),
        ...(numberOfPeople ? { numberOfPeople: numberOfPeople } : {}),
        ...(currency ? { currency: currency } : {}),
        ...(hotelPrice ? { hotelPrice: hotelPrice } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(provider ? { provider: provider } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Recommendations (Hotel)
   * Gets recommendations for Hotels
   * @returns RecommendedHotelsViewModel Success
   * @throws ApiError
   */
  public static async hotelRecommendations(
    {
      body,
    }: {
      body?: HotelRecommendationsRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RecommendedHotelsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<RecommendedHotelsViewModel>({
      method: 'POST',
      url: `/v3/hotels/recommendations`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Availability (Hotel)
   * Gets availability status
   * @returns CheckAvailabilityViewModel Success
   * @throws ApiError
   */
  public static async getAvailability(
    {
      _in,
      out,
      roomId,
    }: {
      _in?: string;
      out?: string;
      roomId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CheckAvailabilityViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<CheckAvailabilityViewModel>({
      method: 'GET',
      url: `/v3/hotels/availability`,
      query: {
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(roomId ? { roomId: roomId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Review Detail
   * Gets reviews for hotel
   * @returns HotelDetailReviewViewModel Success
   * @throws ApiError
   */
  public static async getReviews(
    {
      id,
    }: {
      id: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelDetailReviewViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelDetailReviewViewModel>({
      method: 'GET',
      url: `/v3/hotels/reviews/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotel Events
   * Gets the hotel events
   * @returns HotelEventsPaginatedModel Success
   * @throws ApiError
   */
  public static async getHotelEvents(
    {
      countryCode,
      period,
      page,
      size,
    }: {
      countryCode?: string;
      period?: any;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelEventsPaginatedModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelEventsPaginatedModel>({
      method: 'GET',
      url: `/v3/hotels/events`,
      query: {
        ...(countryCode ? { countryCode: countryCode } : {}),
        ...(period ? { period: period } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotel Provider Urls
   * Gets the hotel urls from provider
   * @returns HotelUrlParserViewModel Success
   * @throws ApiError
   */
  public static async getHotelProviderUrls(
    {
      url,
      provider,
    }: {
      url: string;
      provider: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelUrlParserViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelUrlParserViewModel>({
      method: 'GET',
      url: `/v3/hotels/external`,
      query: {
        ...(url ? { url: url } : {}),
        ...(provider ? { provider: provider } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotel Breadcrumbs
   * Gets hotel listing/detail breadcrumbs
   * @returns BreadcrumbViewModel Success
   * @throws ApiError
   */
  public static async getBreadcrumbs(
    {
      entityId,
      entityType,
    }: {
      entityId: number;
      entityType: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<BreadcrumbViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<BreadcrumbViewModel>>({
      method: 'GET',
      url: `/v3/hotels/breadcrumb`,
      query: {
        ...(entityId ? { entityId: entityId } : {}),
        ...(entityType ? { entityType: entityType } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v3' {
  interface Services {
    HotelsService: typeof HotelsService;
  }
}
